






















































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import numeral from 'numeral';

@Component
export default class FreightFees extends Vue {
    @Prop({ required: true })
    private quotation!: any;

    private get isSeaCondition(): boolean {
        if (!this.quotation) return false;
        const cond = String(this.quotation.declaration_condition || '').toUpperCase().trim();
        const seaConditions = ['LCL', 'FCL', 'FLC', 'ECL', 'EFC'];
        if (cond) {
            return seaConditions.includes(cond) || seaConditions.some((c: string) => cond.includes(c));
        }
        return Boolean(Array.isArray(this.quotation.sea_fees_data) && this.quotation.sea_fees_data.length > 0);
    }

    private get declarationCondition(): string {
        const cond = String((this.quotation && this.quotation.declaration_condition) || '').toUpperCase().trim();
        if (cond.includes('LCL') || cond.includes('ECL')) return 'LCL';
        if (cond.includes('FCL') || cond.includes('FLC') || cond.includes('EFC')) return 'FCL';
        return cond;
    }

    private get hasSellingPrice(): boolean {
        const q = this.quotation || {};
        return Boolean(Number(q.shipping_sell_unit_price) > 0);
    }

    private get hasSeaFees(): boolean {
        return Boolean(this.quotation && Array.isArray(this.quotation.sea_fees_data) && this.quotation.sea_fees_data.length > 0);
    }

    private get unitText(): string {
        return (this.quotation && this.quotation.shipping_fee_basis === 'volume') ? 'm³' : 'kg';
    }

    private getSectionTitle(section: string): string {
        if (section === 'A') return 'A - Chi phí tại cảng TQ';
        if (section === 'B') return 'B - Chi phí tại cảng VN';
        return 'C - Chi phí nội địa VN';
    }

    private getSeaFeesBySection(section: string): any[] {
        if (!this.quotation || !Array.isArray(this.quotation.sea_fees_data)) return [];
        const list = this.quotation.sea_fees_data;
        const targetSection = section.toUpperCase();
        const hasAnySection = list.some((item: any) => Boolean(item && item.section));

        return list.filter((item: any) => {
            if (!item) return false;
            const s = String(item.section || '').toUpperCase().trim();
            if (s) {
                return s === targetSection;
            }
            // Fallback nếu dữ liệu cũ không có thuộc tính section: gộp vào nhóm A
            return !hasAnySection && targetSection === 'A';
        });
    }

    private getRowAmount(row: any): number {
        if (!row) return 0;
        if (row.thanh_tien != null && row.thanh_tien !== '') {
            return Number(row.thanh_tien) || 0;
        }
        if (row.amount != null && row.amount !== '') {
            return Number(row.amount) || 0;
        }
        const qty = Number(row.so_luong || 1);
        const usd = Number(row.don_gia_usd || 0);
        const vnd = Number(row.don_gia_vnd || 0);
        const vat = Number(row.vat_phan_tram != null ? row.vat_phan_tram : (row.vat_percent != null ? row.vat_percent : (row.vat || 0)));
        const discount = Number(row.discount_pct || 0);
        const usdRate = Number((this.quotation && this.quotation.usd_rate) || 25000);

        const baseAmount = (vnd + usd * usdRate) * qty * (1 - discount / 100);
        const amount = ['FCL', 'LCL'].includes(this.declarationCondition) ? baseAmount * (1 + vat / 100) : baseAmount;
        return Math.round(amount);
    }

    private get totalSeaFees(): number {
        if (!this.quotation || !Array.isArray(this.quotation.sea_fees_data)) return 0;
        return this.quotation.sea_fees_data.reduce((sum: number, it: any) => sum + this.getRowAmount(it), 0);
    }

    private get totalFreight(): number {
        const q = this.quotation || {};
        return (
            (Number(q.shipping_fee) || 0) +
            (Number(q.china_domestic_shipping_fee) || 0) +
            (Number(q.vn_domestic_shipping_fee) || 0) +
            (Number(q.loading_unloading_fee) || 0)
        );
    }

    private formatUsd(val: any): string {
        const num = Number(val || 0);
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
    }

    private formatVnd(val: any): string {
        const num = Number(val || 0);
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'VND' }).format(num);
    }

    private formatRowVat(row: any): string {
        if (!row) return '0.00%';
        const val = row.vat_phan_tram != null ? row.vat_phan_tram : (row.vat_percent != null ? row.vat_percent : (row.vat || 0));
        return Number(val || 0).toFixed(2) + '%';
    }

    private formatVat(val: any): string {
        const num = Number(val || 0);
        return num.toFixed(2) + '%';
    }

    private formatCurrency(val: number): string {
        if (!val) return '0 đ';
        return numeral(val).format('0,0') + ' đ';
    }

    private get shippingFeeTableName(): string {
        const q = this.quotation || {};
        return q.shipping_fee_table_name || q.declaration_condition_display || q.declaration_condition || '';
    }

    private get shippingSellPrice(): number {
        const q = this.quotation || {};
        return Number(q.shipping_sell_unit_price) || 0;
    }

    private get entrustmentFeeRate(): number {
        const q = this.quotation || {};
        return Number(q.entrustment_fee_rate != null ? q.entrustment_fee_rate : 0);
    }

    private get shippingSellUnitPriceNote(): string {
        const q = this.quotation || {};
        return Number(q.shipping_sell_unit_price) > 0
            ? "Từ 'shipping_sell_unit_price'"
            : "Chưa thiết lập đơn giá bán cước";
    }

    private get entrustmentFeeRateNote(): string {
        return this.entrustmentFeeRate > 0
            ? 'Từ bảng Entrustment Fee theo bậc giá trị hóa đơn.'
            : 'Chưa xác định % phí ủy thác từ bảng Entrustment Fee (đang hiển thị 0).';
    }

    private get customsSurchargeUnitPriceNote(): string {
        const q = this.quotation || {};
        return q.customs_surcharge_fixed
            ? "Từ 'customs_surcharge_unit_price' cố định"
            : 'Từ Cài đặt Phụ phí tờ khai';
    }

    private get showCustomsSurcharge(): boolean {
        const cond = String((this.quotation && this.quotation.declaration_condition) || '').toUpperCase().trim();
        const allowed = ['ETL', 'ECL', 'LTL1', 'LTL2', 'LTL3', 'LTL4', 'LTL5', 'LCL'];
        return allowed.includes(cond) || Boolean(this.quotation && Number(this.quotation.customs_surcharge_unit_price) > 0);
    }

    private get customsSurchargePrice(): number {
        const q = this.quotation || {};
        if (q.customs_surcharge_unit_price != null && Number(q.customs_surcharge_unit_price) > 0) {
            return Number(q.customs_surcharge_unit_price);
        }
        return 120000;
    }

    private get shippingFeeBasisResolvedText(): string {
        const q = this.quotation || {};
        const resolved = String(q.shipping_fee_basis_resolved || '').toLowerCase();
        if (resolved === 'weight') return 'Theo kg';
        if (resolved === 'volume') return 'Theo m³';
        const basis = String(q.shipping_fee_basis || '').toLowerCase();
        if (basis === 'weight') return 'Theo kg';
        if (basis === 'volume') return 'Theo m³';
        return 'Chưa tính';
    }

    private get isResolvedWeight(): boolean {
        return this.shippingFeeBasisResolvedText === 'Theo kg';
    }

    private get isFeeBasisFixed(): boolean {
        const q = this.quotation || {};
        return Boolean(q.shipping_fee_basis_fixed);
    }

    private get shippingFeeBasisDisplay(): string {
        const q = this.quotation || {};
        const basis = String(q.shipping_fee_basis || 'AUTO').toUpperCase();
        if (basis === 'WEIGHT') return 'Theo kg (Cân nặng)';
        if (basis === 'VOLUME') return 'Theo m³ (Thể tích)';
        return 'Mặc định: Tự động tính (lấy giá cao hơn)';
    }

    private formatNumber(val: number): string {
        if (!val) return '0';
        return numeral(val).format('0,0.[00]');
    }

    private formatRate(val: number): string {
        if (!val) return '0';
        return numeral(val).format('0,0.[00]');
    }
}
